import * as _ from "lodash";
import { useCallback, useEffect } from "react";

import type { WithChildren } from "../../../../_metronic/helpers";
import { useUser } from "../../../contexts/userContext";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import ShareToken from "../../../utils/ShareToken";
import type { ExtraQueryParams } from "./AzureLogin";
import { AzureLogin as internalAzureLogin } from "./AzureLogin";
import {
  refreshTokenAsync,
  signinRedirect,
  userManager,
} from "./AzureLoginHelper";

const AzureLoginVerifier: React.FC<WithChildren> = ({ children }) => {
  window.history.replaceState({}, "");
  const { setUser, setUpdatedUser } = useUser();
  const { storedValue: firstTimeUser } = useLocalStorage<
    Record<string, boolean>
  >("firstTimeUser", {});

  const AzureLogin = async () => {
    const isCallbackUri = window.location.pathname.includes("callback");
    let returnUrl = "";
    if (isCallbackUri) {
      if (window.location.pathname.includes("callbackSignIn")) {
        try {
          const user = await userManager.signinRedirectCallback();
          const state = user?.state as any;
          let isTokenExist = false;
          let token = null as string | null;

          if (state?.returnUrl) {
            returnUrl = state.returnUrl;
          }
          if (state?.token) {
            isTokenExist = true;
            token = state?.token;
          }
          if (user) {
            if (isTokenExist) {
              const encodedToken = token ? encodeURIComponent(token) : "";
              window.location.href =
                "/onboarding?token=" + encodedToken + "&login=success";
            } else {
              window.location.href = returnUrl || "/safety-compliance/cache";
            }
          } else {
            return;
          }
        } catch {
          await signinRedirect("");
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        await userManager.signoutRedirectCallback();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        userManager.removeUser();
        window.location.href = "/auth";
      }
    } else {
      let user = await userManager.getUser();
      if (user) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        internalAzureLogin(
          setUser,
          firstTimeUser[user.profile.sub] === undefined
            ? true
            : firstTimeUser[user.profile.sub],
        );
      } else {
        const path = window.location.pathname + window.location.search;
        const query = window.location.search.substring(1);
        const urlParams = new URLSearchParams(query);
        const token = path.includes("onboarding")
          ? urlParams.get("token")
          : null;
        const extraQueryParams: ExtraQueryParams = {};
        const action = urlParams.get("action");

        if (action === "get-started") {
          for (const value of urlParams.keys()) {
            if (value !== "token") {
              extraQueryParams[value] = urlParams.get(value) || "";
            }
          }
        }

        await signinRedirect(path, extraQueryParams, token);
      }
    }
  };

  const onUserLoaded = useCallback(
    (a: any) => {
      setUpdatedUser(a.profile);
    },
    [setUpdatedUser],
  );

  const onTokenExpiring = async () => {
    if (ShareToken.isMainNode) {
      await refreshTokenAsync();
    }
  };

  useEffect(() => {
    ShareToken.init(refreshTokenAsync);
    userManager.events.addAccessTokenExpiring(onTokenExpiring);

    userManager.events.addUserLoaded(onUserLoaded);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      await AzureLogin();
    })();
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      ShareToken.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="h-100">{children}</div>;
};

export { AzureLoginVerifier };
